import React, { useState, useCallback, useRef, useEffect } from "react"
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import StopIcon from "@mui/icons-material/Stop"
import VideocamIcon from "@mui/icons-material/Videocam"
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord"
import LightbulbIcon from "@mui/icons-material/Lightbulb"
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import useCameraStream from "../../hooks/useCameraStream"
import warpPerspective from "../../utils/warpPerspective"
import { connectToBluetooth, sendClimbToWall } from "../../utils/bluetooth"

// Placement role id 13 = MIDDLE (see client/src/pages/constants.js).
const MIDDLE_ROLE_ID = 13

const API_BASE = process.env.REACT_APP_API_BASE_URL || ""

// Apply a 3x3 homography to a 2D point. Returns [x, y] after homogeneous divide.
const applyH = (H, x, y) => {
  const w = H[2][0] * x + H[2][1] * y + H[2][2]
  return [
    (H[0][0] * x + H[0][1] * y + H[0][2]) / w,
    (H[1][0] * x + H[1][1] * y + H[1][2]) / w,
  ]
}

const invert3x3 = (m) => {
  const [a, b, c] = m[0]
  const [d, e, f] = m[1]
  const [g, h, i] = m[2]
  const det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g)
  if (!det) return null
  return [
    [(e * i - f * h) / det, (c * h - b * i) / det, (b * f - c * e) / det],
    [(f * g - d * i) / det, (a * i - c * g) / det, (c * d - a * f) / det],
    [(d * h - e * g) / det, (b * g - a * h) / det, (a * e - b * d) / det],
  ]
}

const ProjectCamera = () => {
  const [invHomography, setInvHomography] = useState(null)
  const [overlayUrl, setOverlayUrl] = useState(null)
  const [error, setError] = useState(null)
  const [opacity, setOpacity] = useState(0.5)
  const [captureIntervalMs, setCaptureIntervalMs] = useState(250)
  const [diffThreshold, setDiffThreshold] = useState(1)
  const [diffMode, setDiffMode] = useState("last") // "last" | "first"
  const [dwellMs, setDwellMs] = useState(200)
  const [holdRadius, setHoldRadius] = useState(30) // square-space px
  const [selectedHoldIds, setSelectedHoldIds] = useState(() => new Set())
  const [scaledHomography, setScaledHomography] = useState(null)
  const [bluetoothCharacteristic, setBluetoothCharacteristic] = useState(null)
  const [bluetoothConnecting, setBluetoothConnecting] = useState(false)
  const kilterImgRef = useRef(null)
  const filterCanvasRef = useRef(null)
  const prevFrameRef = useRef(null)
  const placementsRef = useRef([])
  const invScaledHomographyRef = useRef(null)
  const dwellRef = useRef({ holdId: null, startedAt: 0, toggled: false })
  const paramsRef = useRef({ dwellMs, holdRadius })

  // Keep paramsRef in sync so the rAF tick reads current slider values without
  // needing to re-subscribe the effect.
  useEffect(() => {
    paramsRef.current = { dwellMs, holdRadius }
  }, [dwellMs, holdRadius])

  const handleConnectBluetooth = useCallback(async () => {
    if (bluetoothCharacteristic || bluetoothConnecting) return
    setBluetoothConnecting(true)
    try {
      const characteristic = await connectToBluetooth()
      if (characteristic) setBluetoothCharacteristic(characteristic)
    } catch (err) {
      console.error("Bluetooth connect failed:", err)
    } finally {
      setBluetoothConnecting(false)
    }
  }, [bluetoothCharacteristic, bluetoothConnecting])

  // Push the current project to the board whenever selection or connection
  // changes. Every hold is sent as MIDDLE for now.
  useEffect(() => {
    if (!bluetoothCharacteristic) return
    const placements = []
    selectedHoldIds.forEach((id) => {
      const p = placementsRef.current.find((pl) => pl.placement_id === id)
      if (p && p.led_position != null) {
        placements.push({ led_position: p.led_position })
      }
    })
    const climb = {
      placements,
      placement_roles: placements.map(() => MIDDLE_ROLE_ID),
    }
    sendClimbToWall(bluetoothCharacteristic, climb).catch((err) =>
      console.error("Failed to send climb to wall:", err)
    )
  }, [bluetoothCharacteristic, selectedHoldIds])

  // Load placements (square-space hold positions) once.
  useEffect(() => {
    fetch(`${API_BASE}/api/admin/hold-color-mapping`)
      .then((r) => r.json())
      .then((data) => {
        placementsRef.current = (data.placements || []).filter(
          (p) => p.in_bounds
        )
      })
      .catch((err) => console.error("Failed to load placements:", err))
  }, [])

  // Pre-load the kilter image once
  useEffect(() => {
    const img = new Image()
    // img.src = "/images/kilter.jpg"
    img.src = "images/kilter-color-holds.png"
    img.onload = () => {
      kilterImgRef.current = img
    }
  }, [])

  // Warp the kilter image whenever we get a new inverse homography
  useEffect(() => {
    if (!invHomography || !kilterImgRef.current) return
    const video = videoRef.current
    if (!video) return
    const w = video.videoWidth || 640
    const h = video.videoHeight || 480
    // The server computed h_square_to_query against a frame downscaled so that
    // its largest axis is 1024 (see downscale() in utils/imageFunctions.js).
    // Scale it back up to the original video resolution.
    const s = Math.max(w, h) / 1024
    const H = invHomography
    const scaledHomography = [
      [H[0][0] * s, H[0][1] * s, H[0][2] * s],
      [H[1][0] * s, H[1][1] * s, H[1][2] * s],
      [H[2][0],     H[2][1],     H[2][2]],
    ]
    const warped = warpPerspective(kilterImgRef.current, scaledHomography, w, h)
    setOverlayUrl(warped.toDataURL("image/png"))
    invScaledHomographyRef.current = invert3x3(scaledHomography)
    setScaledHomography(scaledHomography)
  }, [invHomography])

  const fetchHomography = useCallback(
    async (file, { setLoading, loadingRef }) => {
      setLoading(true)
      loadingRef.current = true
      const formData = new FormData()
      formData.append("image", file)

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL || ""}/api/find_homography`,
          {
            method: "POST",
            body: formData,
          }
        )
        const json = await response.json()
        if (json.error) {
          setError(json.error)
          setInvHomography(null)
          setOverlayUrl(null)
        } else {
          setInvHomography(json.h_square_to_query)
          setError(null)
        }
      } catch (err) {
        console.error(err)
        setError(err.message)
      } finally {
        setLoading(false)
        loadingRef.current = false
      }
    },
    []
  )

  const {
    videoRef,
    canvasRef,
    devices,
    selectedDeviceId,
    handleDeviceChange,
    isCapturing,
    toggleCapture,
    loading,
    videoSize,
  } = useCameraStream({ onFile: fetchHomography, captureIntervalMs })

  // Frame-difference filter — subtract a reference frame from the current
  // frame (per channel, positive differences only) and zero out any pixel
  // whose largest channel diff is below `diffThreshold`. The reference is
  // either the previous frame ("last") or the first frame captured after
  // the filter was enabled ("first").
  useEffect(() => {
    if (diffThreshold === 0) {
      prevFrameRef.current = null
      return
    }
    // Reset whenever the mode changes so "first" captures a fresh reference.
    prevFrameRef.current = null
    let rafId
    const tick = () => {
      const video = videoRef.current
      const canvas = filterCanvasRef.current
      if (video && canvas && video.videoWidth) {
        if (canvas.width !== video.videoWidth) {
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight
          prevFrameRef.current = null
        }
        const ctx = canvas.getContext("2d", { willReadFrequently: true })
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const data = imgData.data
        const prev = prevFrameRef.current
        if (prev && prev.length === data.length) {
          // In "last" mode we need to stash this frame for next tick before
          // we mutate `data`. In "first" mode the reference never changes.
          const currentSnapshot =
            diffMode === "last" ? new Uint8ClampedArray(data) : null
          const w = canvas.width
          let maxVal = 0
          let maxIdx = -1
          for (let i = 0; i < data.length; i += 4) {
            const dr = Math.max(0, data[i] - prev[i])
            const dg = Math.max(0, data[i + 1] - prev[i + 1])
            const db = Math.max(0, data[i + 2] - prev[i + 2])
            const m = Math.max(dr, dg, db)
            if (m < diffThreshold) {
              data[i] = 0
              data[i + 1] = 0
              data[i + 2] = 0
            } else {
              data[i] = dr
              data[i + 1] = dg
              data[i + 2] = db
              if (m > maxVal) {
                maxVal = m
                maxIdx = i
              }
            }
          }
          ctx.putImageData(imgData, 0, 0)
          if (currentSnapshot) prevFrameRef.current = currentSnapshot

          // Laser → hold dwell detection. Brightest above-threshold pixel is
          // treated as the laser. Project it into square space, find the
          // nearest placement, and toggle selection after dwellMs on the same
          // hold.
          const invH = invScaledHomographyRef.current
          const placements = placementsRef.current
          const { dwellMs: dMs, holdRadius: rad } = paramsRef.current
          if (maxIdx >= 0 && invH && placements.length) {
            const px = (maxIdx / 4) % w
            const py = Math.floor(maxIdx / 4 / w)
            const [sx, sy] = applyH(invH, px, py)
            const r2 = rad * rad
            let best = null
            let bestDist = r2
            for (let j = 0; j < placements.length; j++) {
              const p = placements[j]
              const dx = p.square_x - sx
              const dy = p.square_y - sy
              const d2 = dx * dx + dy * dy
              if (d2 < bestDist) {
                bestDist = d2
                best = p
              }
            }
            const now = performance.now()
            const dwell = dwellRef.current
            if (!best) {
              dwellRef.current = { holdId: null, startedAt: 0, toggled: false }
            } else if (best.placement_id !== dwell.holdId) {
              dwellRef.current = {
                holdId: best.placement_id,
                startedAt: now,
                toggled: false,
              }
            } else if (!dwell.toggled && now - dwell.startedAt >= dMs) {
              dwellRef.current = { ...dwell, toggled: true }
              setSelectedHoldIds((prev) => {
                const next = new Set(prev)
                if (next.has(best.placement_id)) next.delete(best.placement_id)
                else next.add(best.placement_id)
                console.log(
                  `[dwell] toggled placement ${best.placement_id} (hole ${best.hole_id}) — selection:`,
                  Array.from(next)
                )
                return next
              })
            }
          } else {
            dwellRef.current = { holdId: null, startedAt: 0, toggled: false }
          }
        } else {
          // First frame: nothing to diff against, just stash and show black.
          prevFrameRef.current = new Uint8ClampedArray(data)
          for (let i = 0; i < data.length; i += 4) {
            data[i] = 0
            data[i + 1] = 0
            data[i + 2] = 0
          }
          ctx.putImageData(imgData, 0, 0)
        }
      }
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [diffThreshold, diffMode, videoRef])

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
      }}
    >
      {/* Controls bar */}
      <Card sx={{ width: "100%", maxWidth: 640 }}>
        <CardContent
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2,
            "&:last-child": { pb: 2 },
            flexWrap: "wrap",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel>Camera</InputLabel>
            <Select
              value={selectedDeviceId}
              onChange={handleDeviceChange}
              label="Camera"
            >
              {devices.map((device) => (
                <MenuItem key={device.deviceId} value={device.deviceId}>
                  {device.label || `Camera ${device.deviceId.slice(0, 8)}...`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Interval</InputLabel>
            <Select
              value={captureIntervalMs}
              onChange={(e) => setCaptureIntervalMs(e.target.value)}
              label="Interval"
            >
              <MenuItem value={100}>100 ms</MenuItem>
              <MenuItem value={250}>250 ms</MenuItem>
              <MenuItem value={500}>500 ms</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant={isCapturing ? "outlined" : "contained"}
            onClick={toggleCapture}
            startIcon={isCapturing ? <StopIcon /> : <PlayArrowIcon />}
            color={isCapturing ? "error" : "primary"}
            sx={{ minWidth: 120 }}
          >
            {isCapturing ? "Stop" : "Start"}
          </Button>

          {isCapturing && (
            <FiberManualRecordIcon
              sx={(theme) => ({
                color: theme.palette.error.main,
                fontSize: 14,
                animation: "pulse 1.5s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.3 },
                },
              })}
            />
          )}

          <Tooltip
            title={
              bluetoothCharacteristic
                ? "Connected to board"
                : bluetoothConnecting
                ? "Connecting..."
                : "Connect to board"
            }
          >
            <span>
              <IconButton
                size="small"
                onClick={handleConnectBluetooth}
                disabled={
                  !!bluetoothCharacteristic || bluetoothConnecting
                }
                color={bluetoothCharacteristic ? "warning" : "default"}
              >
                {bluetoothCharacteristic ? (
                  <LightbulbIcon />
                ) : (
                  <LightbulbOutlinedIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </CardContent>
      </Card>

      {/* Video feed with overlay */}
      <Card
        sx={{
          width: "100%",
          maxWidth: videoSize.width || 640,
          overflow: "hidden",
        }}
      >
        <Box sx={{ position: "relative", lineHeight: 0 }}>
          <Box
            component="video"
            ref={videoRef}
            autoPlay
            playsInline
            muted
            sx={{
              width: "100%",
              display: "block",
              bgcolor: "background.default",
              minHeight: 200,
            }}
          />

          {diffThreshold > 0 && (
            <Box
              component="canvas"
              ref={filterCanvasRef}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
            />
          )}

          {overlayUrl && (
            <Box
              component="img"
              src={overlayUrl}
              alt="Kilter overlay"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "fill",
                opacity: opacity,
                pointerEvents: "none",
              }}
            />
          )}

          {scaledHomography && videoSize.width > 0 && (
            <Box
              component="svg"
              viewBox={`0 0 ${videoSize.width} ${videoSize.height}`}
              preserveAspectRatio="none"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
            >
              {Array.from(selectedHoldIds).map((id) => {
                const p = placementsRef.current.find(
                  (pl) => pl.placement_id === id
                )
                if (!p) return null
                const [cx, cy] = applyH(
                  scaledHomography,
                  p.square_x,
                  p.square_y
                )
                // Project a point offset by holdRadius in square space to get
                // a reasonable camera-space radius (square→camera scale).
                const [cx2, cy2] = applyH(
                  scaledHomography,
                  p.square_x + holdRadius,
                  p.square_y
                )
                const r = Math.hypot(cx2 - cx, cy2 - cy)
                return (
                  <circle
                    key={id}
                    cx={cx}
                    cy={cy}
                    r={r}
                    fill="cyan"
                    fillOpacity={0.5}
                    stroke="white"
                    strokeWidth={2}
                  />
                )
              })}
            </Box>
          )}

          {!isCapturing && !overlayUrl && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                color: "text.secondary",
                bgcolor: "rgba(0,0,0,0.5)",
              }}
            >
              <VideocamIcon sx={{ fontSize: 56, opacity: 0.5 }} />
              <Typography variant="body1">
                Select a camera and press Start
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.5 }}>
                Kilter board overlay will appear here
              </Typography>
            </Box>
          )}
        </Box>

        {overlayUrl && (
          <Box sx={{ px: 3, py: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, fontSize: "0.8rem" }}
            >
              Overlay opacity
            </Typography>
            <Slider
              value={opacity}
              onChange={(e, v) => setOpacity(v)}
              min={0}
              max={1}
              step={0.01}
              size="small"
            />
          </Box>
        )}

        <Box sx={{ px: 3, py: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.8rem" }}
            >
              Frame-diff threshold ({diffThreshold})
            </Typography>
            <ToggleButtonGroup
              value={diffMode}
              exclusive
              size="small"
              onChange={(e, v) => {
                if (v) setDiffMode(v)
              }}
            >
              <ToggleButton value="last" sx={{ py: 0.25, px: 1 }}>
                Last frame
              </ToggleButton>
              <ToggleButton value="first" sx={{ py: 0.25, px: 1 }}>
                First frame
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Slider
            value={diffThreshold}
            onChange={(e, v) => setDiffThreshold(v)}
            min={0}
            max={255}
            step={1}
            size="small"
          />
        </Box>

        <Box sx={{ px: 3, py: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.8rem" }}
            >
              Dwell time ({dwellMs} ms)
            </Typography>
            <Button
              size="small"
              onClick={() => setSelectedHoldIds(new Set())}
              disabled={selectedHoldIds.size === 0}
            >
              Clear ({selectedHoldIds.size})
            </Button>
          </Box>
          <Slider
            value={dwellMs}
            onChange={(e, v) => setDwellMs(v)}
            min={100}
            max={2000}
            step={50}
            size="small"
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.8rem", mt: 1 }}
          >
            Hold radius ({holdRadius} px)
          </Typography>
          <Slider
            value={holdRadius}
            onChange={(e, v) => setHoldRadius(v)}
            min={10}
            max={80}
            step={1}
            size="small"
          />
        </Box>
      </Card>

      <canvas ref={canvasRef} style={{ display: "none" }} />

      {/* Status indicators */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            color: "text.secondary",
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2">Computing homography...</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ width: "100%", maxWidth: 640 }}>
          {error}
        </Alert>
      )}
    </Box>
  )
}

export default ProjectCamera
